// ============================================================
// THE STEALTH PLATES - what the drum holds while the table is in Stealth.
//
// The same revolver as Combat's, loaded differently. No costs are charged
// here that mean anything (there are no rounds), but every row carries one
// because a plate always says what it would cost in a fight.
//
// QUIET WORK chambers like any decisive action and the trigger fires it:
//
//   - Takedown fires the armed weapon. It is an attack; what makes it a
//     takedown is the target not having noticed (+30, no Dodge, no Parry),
//     which the scene supplies - so the plate asks for an unaware target
//     and adds nothing of its own.
//   - The rest are tests (`quiet`): what the one acting rolls against what
//     the sharpest of the opposition answers with. They are put to the GM,
//     whose screen rolls them and proposes what the result does to the
//     ladder (`engine/stealth/proposals`).
//
// Go loud is not a test at all: it asks the GM for the alarm.
//
// Order is presentation order and section order, as in the combat catalog.
// ============================================================
import type { ActionDef } from './types';

const AWARENESS = { kind: 'skill', id: 'awareness' } as const;

// Each quiet row's `beat` is its gesture on the map (Z1): a word for the attempt, never the result - the roll is
// the GM's secret.
export const STEALTH_ROWS: ActionDef[] = [
  {
    id: 'takedown',
    name: 'Takedown',
    cost: 'half',
    category: 'attack',
    execution: 'chamber',
    section: 'quiet',
    fires: 'attack',
    requiresTarget: true,
    requiresUnawareTarget: true,
    blurb: 'Strike someone unaware: +30 to hit, no Dodge, no Parry.',
  },
  {
    id: 'moveUnseen',
    name: 'Move unseen',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'quiet',
    fires: 'quiet',
    requiresActor: true,
    beat: { plays: 'moveUnseen', verb: 'SNEAKING' },
    quiet: { move: 'moveUnseen', actor: { kind: 'skill', id: 'silent_move' }, defender: AWARENESS },
    blurb: 'Silent Move against Awareness.',
  },
  {
    id: 'hide',
    name: 'Hide',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'quiet',
    fires: 'quiet',
    requiresActor: true,
    beat: { plays: 'hide', verb: 'HIDING' },
    quiet: { move: 'hide', actor: { kind: 'skill', id: 'concealment' }, defender: AWARENESS },
    blurb: 'Go to ground: Concealment against Awareness.',
  },
  {
    id: 'observe',
    name: 'Observe',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'quiet',
    fires: 'quiet',
    requiresActor: true,
    beat: { plays: 'observe', verb: 'WATCHING' },
    quiet: { move: 'observe', actor: AWARENESS, defender: null },
    blurb: 'Watch and learn: an Awareness test.',
  },
  {
    id: 'distract',
    name: 'Distract',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'quiet',
    fires: 'quiet',
    requiresActor: true,
    beat: { plays: 'distract', verb: 'A NOISE' },
    quiet: { move: 'distract', actor: { kind: 'skill', id: 'deceive' }, defender: { kind: 'skill', id: 'scrutiny' } },
    blurb: 'Draw a sentry off: Deceive against Scrutiny.',
  },
  {
    id: 'shadow',
    name: 'Shadow',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'quiet',
    fires: 'quiet',
    requiresActor: true,
    beat: { plays: 'shadow', verb: 'FOLLOWING' },
    quiet: { move: 'shadow', actor: { kind: 'skill', id: 'shadowing' }, defender: AWARENESS },
    blurb: 'Follow without being seen: Shadowing against Awareness.',
  },
  {
    id: 'goLoud',
    name: 'Go loud',
    cost: 'free',
    category: 'utility',
    execution: 'instant',
    section: 'leave',
    blurb: 'Give up on quiet. Asks the GM; the fight starts on their word.',
  },
];
